import React, { useState, useEffect, useCallback, useMemo } from "react";
import { MdClose } from "react-icons/md";
import media from "styled-media-query";
import dayjs from "dayjs";
import DayPicker, { DateUtils, LocaleUtils } from "react-day-picker";
import "react-day-picker/lib/style.css";
import isToday from "dayjs/plugin/isToday";
import styled from "styled-components";
import sort from "fast-sort";

import { ShopEntity } from "src/firebase/db/Shops";
import { BakedBreadEntity } from "src/firebase/db/BakedBreads";
import { BakedBreadTagEntity } from "src/firebase/db/BakedBreadTags";
import { OrderBreadEntity } from "src/firebase/db/OrderBreads";
import { NotificationEntity } from "src/firebase/db/Notifications";

import { getShopFullAddress } from "src/helpers/ShopHelper";
import { formatDateToDate } from "src/helpers/UtilityHelper";

import StyleConfig from "src/constants/StyleConfig";
import { State } from "src/state/ducks";

import Heading from "src/components/atoms/typo/Heading";
import InputForm from "src/components/atoms/form/InputForm";
import SelectBox from "src/components/atoms/form/SelectBox";
import {
  MyDatePicker,
  MyTimePicker,
} from "src/components/atoms/form/MyDatePicker";
import Button from "src/components/atoms/button/Button";
import PlainHomeButton from "src/components/atoms/button/PlainHomeButton";
import TextArea from "src/components/atoms/form/TextArea";
import { ValidateMessageIfError } from "src/components/atoms/form/ErrorMessage";
import SingleInputFormBlock from "src/components/molecules/form/SingleInputFormBlock";
import InputLabelHolder from "src/components/molecules/form/InputLabelHolder";
import UploadImageBlock from "src/components/organisms/form/UploadImageBlock";

dayjs.extend(isToday);

const dateFormat = "yyyy年MM月dd日(EEEEE)";
const timeFormat = "HH:mm";

const parseNumToStr = (v: any) => (typeof v === "number" ? v.toString() : v);
const parseStrToNum = (v: any) => (isNaN(v) ? 0 : parseInt(v));

const mapBakedBreadEntityToState = (bakedBread: BakedBreadEntity) => {
  return {
    image: bakedBread.image || "",
    name: bakedBread.name || "",
    price: bakedBread.price || (("" as unknown) as string),
    description: bakedBread.description || "",
    width: parseStrToNum(bakedBread.width) || "",
    depth: parseStrToNum(bakedBread.depth) || "",
    height: parseStrToNum(bakedBread.height) || "",
    bestBefore: bakedBread.best_before || "",
    shopId: bakedBread.shop_id,
    // bakedBreadTagId: bakedBread.baked_bread_tags?.length ? bakedBread.baked_bread_tags[0].id : '',
    bakedBreadTagId: "",
  };
};

const mapBakedBreadStateToEntity = (
  state: ReturnType<typeof mapBakedBreadEntityToState>,
  bakedBreadTags: BakedBreadTagEntity[]
) => {
  return {
    image: state.image,
    name: state.name,
    price: +state.price,
    description: state.description,
    width: parseNumToStr(state.width) || "",
    depth: parseNumToStr(state.depth) || "",
    height: parseNumToStr(state.height) || "",
    best_before: state.bestBefore,
    shop_id: state.shopId,
    is_reservation: true,
    // baked_bread_tags: bakedBreadTags.filter(({ id }) => id === state.bakedBreadTagId) || [],
    baked_bread_tags: [],
  };
};

const mapOrderBreadEntityToState = (orderBread: OrderBreadEntity) => ({
  quantity: orderBread.stock,
  maximumOrder: orderBread.maximum_order,
});

const mapOrderBreadStateToEntity = (
  state: ReturnType<typeof mapOrderBreadEntityToState>
) => ({
  stock: state.quantity,
  maximum_order: state.maximumOrder,
});

const mapNotificationEntityToState = (notification: NotificationEntity) => {
  const { last_pickup_days, pickup_supplement } = notification;

  type timeProps = "published_time" | "scheduled_time" | "can_order_time";
  // 日付だけ取得
  const getDate = (key: timeProps) =>
    notification[key] ? formatDateToDate(notification[key]) : undefined;
  // 時間だけ取得
  const getTime = (key: timeProps) => {
    if (!notification[key]) {
      return dayjs().hour(0).minute(0).second(0).toDate();
    }
    const d = dayjs(formatDateToDate(notification[key]));
    return dayjs()
      .hour(d.hour())
      .minute(d.minute())
      .second(d.second())
      .toDate();
  };
  const lastPickupDays = () => {
    type daysProps = (Date | undefined)[];
    const values = last_pickup_days as daysProps;
    if (!Array.isArray(last_pickup_days) || !last_pickup_days.length) {
      return [] as daysProps;
    }
    return values.filter((v) => v).map((v) => formatDateToDate(v)) as daysProps;
  };
  return {
    publishedDate: getDate("published_time"),
    publishedTime: getTime("published_time"),
    scheduledDate: getDate("scheduled_time"),
    scheduledTime: getTime("scheduled_time"),
    canOrderDate: getDate("can_order_time"),
    canOrderTime: getTime("can_order_time"),
    lastPickupDays: lastPickupDays(),
    pickupSupplement: pickup_supplement,
  };
};

const mapNotificationStateToEntity = (
  state: ReturnType<typeof mapNotificationEntityToState>
) => {
  const {
    publishedDate,
    publishedTime,
    scheduledDate,
    scheduledTime,
    canOrderDate,
    canOrderTime,
    lastPickupDays,
    pickupSupplement,
  } = state;

  const setTimeToDate = (yyyydd: Date, hhmm: Date) => {
    const date = dayjs(yyyydd);
    const time = dayjs(hhmm);
    return date.hour(time.hour()).minute(time.minute()).second(0).toDate();
  };

  return {
    published_time:
      publishedDate && publishedTime
        ? setTimeToDate(publishedDate, publishedTime)
        : undefined,
    scheduled_time:
      scheduledDate && scheduledTime
        ? setTimeToDate(scheduledDate, scheduledTime)
        : undefined,
    can_order_time:
      canOrderDate && canOrderTime
        ? setTimeToDate(canOrderDate, canOrderTime)
        : undefined,
    last_pickup_days: lastPickupDays.filter((v) => v) as Date[],
    pickup_supplement: pickupSupplement,
  };
};

type Props = {
  id?: string;
  notification: NotificationEntity;
  orderBread: OrderBreadEntity;
  bakedBread: BakedBreadEntity;
  bakedBreadTags: BakedBreadTagEntity[];
  shops: ShopEntity[];
  errors: State["notification"]["reservations"]["validationErrors"];
  onSubmit: (payload: {
    bakedBread: BakedBreadEntity;
    orderBread: OrderBreadEntity;
    notification: NotificationEntity;
  }) => void;
  onSave: (payload: {
    bakedBread: BakedBreadEntity;
    orderBread: OrderBreadEntity;
    notification: NotificationEntity;
  }) => void;
  onEmpty: () => void;
};

const ReservationCreateForm = (props: Props) => {
  const {
    id,
    orderBread,
    bakedBread,
    bakedBreadTags,
    notification,
    shops,
    onSubmit,
    onSave,
    onEmpty,
    errors,
  } = props;

  const today = new Date();
  const minTime = dayjs(today).hour(0).minute(0).second(0).toDate();
  const maxTime = dayjs(today).hour(23).minute(59).second(59).toDate();

  const [bakedBreadState, setBakedBreadState] = useState(
    mapBakedBreadEntityToState(bakedBread)
  );

  const [orderBreadState, setOrderBreadState] = useState(
    mapOrderBreadEntityToState(orderBread)
  );

  const [notificationState, setNotificationState] = useState(
    mapNotificationEntityToState(notification)
  );

  // 初期化
  useEffect(() => {
    // shop_idは処理上必須なので、初期値を設定する
    if (!id) {
      let initialShopId = shops.length ? shops[0].id : "";
      setBakedBreadState((prev) => ({ ...prev, shopId: initialShopId }));
      return;
    }

    setBakedBreadState(
      mapBakedBreadEntityToState({
        ...bakedBread,
        shop_id: notification.shop_id,
      })
    );
    setOrderBreadState(mapOrderBreadEntityToState(orderBread));
    setNotificationState(mapNotificationEntityToState(notification));
  }, [id, shops, bakedBread, orderBread, notification, setBakedBreadState]);

  const {
    name,
    price,
    image,
    description,
    width,
    depth,
    height,
    bestBefore,
    shopId,
    bakedBreadTagId,
  } = bakedBreadState;

  const { quantity, maximumOrder } = orderBreadState;

  const {
    publishedDate,
    publishedTime,
    scheduledDate,
    scheduledTime,
    canOrderDate,
    canOrderTime,
    lastPickupDays,
    pickupSupplement,
  } = notificationState;

  const isAllEmpty = useMemo(
    () =>
      !name &&
      !price &&
      !image &&
      !description &&
      !width &&
      !depth &&
      !height &&
      !bestBefore &&
      !bakedBreadTagId &&
      !quantity &&
      !maximumOrder &&
      !publishedDate &&
      !scheduledDate &&
      !canOrderDate &&
      lastPickupDays.every((v) => !v) &&
      !pickupSupplement,
    [
      name,
      price,
      image,
      description,
      width,
      depth,
      height,
      bestBefore,
      bakedBreadTagId,
      quantity,
      maximumOrder,
      publishedDate,
      scheduledDate,
      canOrderDate,
      lastPickupDays,
      pickupSupplement,
    ]
  );

  // 商品画像
  const thumbnails = useMemo(() => (image ? [image] : []), [image]);

  // 対象店舗
  const shopOptions = useMemo(
    () => shops.map((shop) => ({ value: shop.id, content: shop.name })),
    [shops]
  );

  // カテゴリ情報
  const bakedBreadTagOptions = useMemo(
    () => bakedBreadTags.map(({ id, name }) => ({ value: id, content: name })),
    [bakedBreadTags]
  );

  // 対象店舗情報
  const shop = useMemo(() => {
    const shop = shops.find((v) => v.id === shopId) as ShopEntity;
    return {
      shop_id: shopId,
      shop_address: shop ? getShopFullAddress(shop) : "",
      shop_name: shop ? shop.name : "",
    };
  }, [shops, shopId]);

  // 個数が未入力の場合は最大購入数をdisabled
  const isDisabledMaximumOrder = useMemo(() => quantity === 0, [quantity]);

  // day-pickerの選択可能範囲
  const disabledLastPickupDays = useMemo(() => {
    if (!publishedDate || !scheduledDate) {
      return [{ before: today }, { after: today }];
    }
    return scheduledDate
      ? [
          { before: dayjs(scheduledDate).add(2, "days").toDate() },
          { after: dayjs("2021-04-16").toDate() },
        ]
      : [];
  }, [publishedDate, scheduledDate, today]);

  const initialLastPickupMonth = useMemo(() => {
    if (!lastPickupDays.length) {
      if (scheduledDate) {
        return scheduledDate;
      }
      return publishedDate ? publishedDate : today;
    }
    const dates = sort(lastPickupDays).asc();
    return dates[0] || today;
  }, [scheduledDate, publishedDate, lastPickupDays, today]);

  // 日付選択の最小値
  const minDate = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        return process.env.REACT_APP_STAGE === "dev"
          ? today
          : dayjs("2020-11-05").toDate();
        // return d.toDate();
      }
      return dayjs(date).hour(0).minute(0).second(0).toDate();
    },
    [today]
  );

  // 在庫数
  const quantityOptions = useCallback(() => {
    const options = [...Array(2000)].map((_, i) => ({
      value: `${i + 1}`,
      content: `${i + 1}個`,
    }));
    options.unshift({ value: "0", content: "総販売数を入力してください" });
    return options;
  }, []);

  // お一人様あたり
  const maximumOrderOptions = useCallback(() => {
    const options = [...Array(quantity)].map((_, i) => ({
      value: `${i + 1}`,
      content: `${i + 1}個まで`,
    }));
    options.unshift({ value: "0", content: "最大数を入力してください" });
    return options;
  }, [quantity]);

  // 画像アップロード
  const onChangeUpload = useCallback(
    (images: string[]) => {
      setBakedBreadState((prev) => ({
        ...prev,
        image: images.find((v) => v) || "",
      }));
    },
    [setBakedBreadState]
  );

  // 在庫が最大注文数を下回った場合の処理
  const onChangeQuantity = useCallback(
    (num: number | null) => {
      const quantity = num || 0;

      setOrderBreadState((prev) => ({ ...prev, quantity }));
      if (maximumOrder > quantity) {
        setOrderBreadState((prev) => ({ ...prev, maximumOrder: quantity }));
      }
    },
    [maximumOrder, setOrderBreadState]
  );

  // 事前告知が変更された時
  const onChangePublishedDate = useCallback(
    (date: Date) => {
      setNotificationState((prev) => ({
        ...prev,
        publishedDate: date,
        scheduledTime: scheduledTime ? scheduledTime : minTime,
      }));
    },
    [minTime, scheduledTime]
  );

  // 購入可能日時が変更された時
  const onChangeScheduledDate = useCallback(
    (date: Date) => {
      setNotificationState((prev) => ({
        ...prev,
        scheduledDate: date,
        scheduledTime: scheduledTime ? scheduledTime : minTime,
        canOrderTime: canOrderTime ? canOrderTime : minTime,
      }));
    },
    [scheduledTime, minTime, canOrderTime]
  );

  // 受け渡し日の値を変更
  const onClickLastPickupDay = useCallback(
    (day: Date, selected?: boolean, disabled?: boolean) => {
      if (disabled) {
        return;
      }
      let days = lastPickupDays.filter((v) => v);
      if (selected) {
        const selectedIndex = lastPickupDays.findIndex((selectedDay) =>
          selectedDay ? DateUtils.isSameDay(selectedDay, day) : false
        );
        days = days.filter((v, i) => i !== selectedIndex);
      } else {
        days.push(day);
      }
      setNotificationState((prev) => ({
        ...prev,
        lastPickupDays: sort(days).asc(),
      }));
    },
    [lastPickupDays, setNotificationState]
  );

  // 受け渡し日の入力欄を追加
  const onClickLastPickupDayRemove = useCallback(
    (index: number) => {
      setNotificationState((prev) => {
        const { lastPickupDays } = prev;
        return {
          ...prev,
          lastPickupDays: lastPickupDays.filter((v, i) => i !== index),
        };
      });
    },
    [setNotificationState]
  );

  const wrapOnSubmit = useCallback(() => {
    const formBakedBread = mapBakedBreadStateToEntity(
      bakedBreadState,
      bakedBreadTags
    );
    const formOrderBread = mapOrderBreadStateToEntity(orderBreadState);
    const formnotification = mapNotificationStateToEntity(notificationState);
    onSubmit({
      bakedBread: {
        ...bakedBread,
        ...shop,
        ...formBakedBread,
        is_reservation: true,
      },
      orderBread: {
        ...orderBread,
        ...shop,
        ...formBakedBread,
        ...formOrderBread,
        is_reservation: true,
      },
      notification: {
        ...notification,
        ...shop,
        ...formnotification,
        is_reservation: true,
        is_draft: false,
      },
    });
  }, [
    shop,
    bakedBread,
    bakedBreadTags,
    orderBread,
    notification,
    bakedBreadState,
    orderBreadState,
    notificationState,
    onSubmit,
  ]);

  const wrapOnSave = useCallback(() => {
    if (isAllEmpty) {
      onEmpty();
      return;
    }

    const formBakedBread = mapBakedBreadStateToEntity(
      bakedBreadState,
      bakedBreadTags
    );
    const formOrderBread = mapOrderBreadStateToEntity(orderBreadState);
    const formnotification = mapNotificationStateToEntity(notificationState);
    onSave({
      bakedBread: {
        ...bakedBread,
        ...shop,
        ...formBakedBread,
        is_reservation: true,
      },
      orderBread: {
        ...orderBread,
        ...shop,
        ...formBakedBread,
        ...formOrderBread,
        is_reservation: true,
      },
      notification: {
        ...notification,
        ...shop,
        ...formnotification,
        is_reservation: true,
        is_draft: true,
      },
    });
  }, [
    shop,
    bakedBread,
    bakedBreadTags,
    orderBread,
    notification,
    bakedBreadState,
    orderBreadState,
    notificationState,
    onSave,
    onEmpty,
    isAllEmpty,
  ]);

  return (
    <Container>
      <ValidateMessageIfError
        marginVertical={8}
        fontSize={16}
        messages={errors.scheduled_time_top}
      />
      <div className="form-bakedBread">
        <FormItem>
          <UploadImageBlock
            label="商品画像"
            thumbnails={thumbnails}
            onChange={onChangeUpload}
            canUploadImageLength={1}
          />
          <ValidateMessageIfError marginVertical={8} messages={errors.image} />
        </FormItem>
        <FormItem>
          <InputLabelHolder labelName="対象店舗" isRequired />
          <SelectBox
            id="shop"
            placeholder=""
            color="green"
            inputValue={shopId}
            onChange={(shopId: string) =>
              setBakedBreadState((prev) => ({ ...prev, shopId }))
            }
            options={shopOptions}
          />
          <ValidateMessageIfError
            marginVertical={8}
            messages={errors.shop_id}
          />
        </FormItem>
        <FormItem>
          <SingleInputFormBlock
            isRequired={true}
            labelName="商品名"
            inputType="text"
            inputValue={name}
            placeholder="商品名を入力してください"
            onChange={(name) =>
              setBakedBreadState((prev) => ({ ...prev, name }))
            }
            isError={!!errors.name}
            isDisabeld={false}
          />
          <ValidateMessageIfError marginVertical={8} messages={errors.name} />
        </FormItem>
        <div className="is-hidden">
          <FormItem>
            <InputLabelHolder labelName="カテゴリー" isRequired />
            <SelectBox
              id="bakedBreadTag"
              placeholder=""
              inputValue={bakedBreadTagId}
              isError={!!errors.baked_bread_tags}
              onChange={(bakedBreadTagId) =>
                setBakedBreadState((prev) => ({ ...prev, bakedBreadTagId }))
              }
              options={bakedBreadTagOptions}
            />
            <ValidateMessageIfError
              marginVertical={8}
              messages={errors.baked_bread_tags}
            />
          </FormItem>
        </div>
        <FormItem>
          <SingleInputFormBlock
            isRequired={true}
            labelName="販売単価（税抜）"
            inputType="text"
            inputValue={price}
            placeholder="価格を入力してください"
            onChange={(price) =>
              setBakedBreadState((prev) => ({ ...prev, price: price }))
            }
            isError={!!errors.price}
            isDisabeld={false}
          />
          <ValidateMessageIfError marginVertical={8} messages={errors.price} />
        </FormItem>
        <FormItem>
          <InputLabelHolder labelName="商品説明" isRequired />
          <TextArea
            rows={10}
            max={280}
            inputValue={description}
            placeholder="商品の魅力を記入してください"
            onChange={(v) =>
              setBakedBreadState((prev) => ({
                ...prev,
                description: v.slice(0, 280),
              }))
            }
            isError={!!errors.description}
            isDisabeld={false}
          />
          <ValidateMessageIfError
            marginVertical={8}
            messages={errors.description}
          />
        </FormItem>
        <FormItem>
          <InputLabelHolder labelName="商品サイズ（cm）" isRequired={false} />
          <div className="form-triple">
            <InputForm
              inputType="number"
              inputValue={width}
              placeholder="幅"
              onChange={(width) =>
                setBakedBreadState((prev) => ({
                  ...prev,
                  width: parseInt(width),
                }))
              }
              isError={!!errors.width}
              isDisabeld={false}
              min={0}
            />
            <InputForm
              inputType="number"
              inputValue={depth}
              placeholder="奥行き"
              onChange={(depth) =>
                setBakedBreadState((prev) => ({
                  ...prev,
                  depth: parseInt(depth),
                }))
              }
              isError={!!errors.depth}
              isDisabeld={false}
              min={0}
            />
            <InputForm
              inputType="number"
              inputValue={height}
              placeholder="高さ"
              onChange={(height) =>
                setBakedBreadState((prev) => ({
                  ...prev,
                  height: parseInt(height),
                }))
              }
              isError={!!errors.height}
              isDisabeld={false}
              min={0}
            />
          </div>
          {/* <ValidateMessageIfError marginVertical={8} messages={errors.description} /> */}
        </FormItem>
        <FormItem>
          <SingleInputFormBlock
            isRequired={true}
            labelName="賞味期限"
            inputType="text"
            inputValue={bestBefore}
            placeholder="賞味期限を入力してください"
            onChange={(bestBefore) =>
              setBakedBreadState((prev) => ({ ...prev, bestBefore }))
            }
            isError={!!errors.best_before}
            isDisabeld={false}
          />
          <ValidateMessageIfError
            marginVertical={8}
            messages={errors.best_before}
          />
        </FormItem>
      </div>

      <Heading tag="h2" text="sacriでの総販売数を選択してください" />
      <FormItem>
        <InputLabelHolder labelName=" 総販売数を選択してください" isRequired />
        <SelectBox
          id="quantity"
          showDefaultOption={false}
          inputValue={quantity}
          onChange={(num) => onChangeQuantity(parseInt(num))}
          options={quantityOptions()}
          color="green"
        />
        <ValidateMessageIfError marginVertical={8} messages={errors.stock} />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="一人のお客さまが購入できる最大数を選択してください"
          isRequired
        />
        <SelectBox
          id="maximumOrder"
          showDefaultOption={false}
          inputValue={maximumOrder}
          onChange={(num) =>
            setOrderBreadState((prev) => ({
              ...prev,
              maximumOrder: parseInt(num),
            }))
          }
          options={maximumOrderOptions()}
          disabled={isDisabledMaximumOrder}
          color="green"
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.maximum_order}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="事前告知開始日時を選択してください"
          isRequired={true}
        />
        <p className="annotation">
          ※この日時からsacri上に表示され、予約注文開始のお知らせ通知をONにできます
        </p>
        <div className="form-double">
          <div className="form-date">
            <MyDatePicker
              className="datepicker"
              date={publishedDate}
              placeholder="日付を選択"
              dateFormat={dateFormat}
              onChange={(v) => onChangePublishedDate(v)}
              isDisabled={false}
              showTimeSelect={false}
              minDate={minDate(undefined)}
            />
          </div>
          <div className="form-time">
            <MyTimePicker
              date={publishedDate ? publishedTime : undefined}
              placeholder="時間を選択"
              dateFormat={timeFormat}
              onChange={(publishedTime) =>
                setNotificationState((prev) => ({ ...prev, publishedTime }))
              }
              isDisabled={!publishedDate}
              showTimeSelect={false}
              minTime={minTime}
              maxTime={maxTime}
            />
          </div>
        </div>
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.published_time}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="予約注文開始日時（注文解禁日）を選択してください"
          isRequired={true}
        />
        <p className="annotation">
          ※この日時からsacri上で予約注文と事前決済を行えるようになります
        </p>
        <div className="form-double">
          <div className="form-date">
            <MyDatePicker
              className="datepicker"
              date={scheduledDate}
              placeholder="日付を選択"
              dateFormat={dateFormat}
              onChange={(v) => onChangeScheduledDate(v)}
              isDisabled={!publishedDate}
              showTimeSelect={false}
              minDate={minDate(publishedDate)}
            />
          </div>
          <div className="form-time">
            <MyTimePicker
              date={scheduledDate ? scheduledTime : undefined}
              placeholder="時間を選択"
              dateFormat={timeFormat}
              onChange={(scheduledTime) =>
                setNotificationState((prev) => ({ ...prev, scheduledTime }))
              }
              isDisabled={!scheduledDate}
              showTimeSelect={false}
              minTime={minTime}
              maxTime={maxTime}
            />
          </div>
        </div>
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.scheduled_time}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="ラストオーダー日時を選択してください"
          isRequired={true}
        />
        <p className="annotation">
          ※この日時以降はsacri上で受付終了と表示され、予約注文を行えなくなります
        </p>
        <div className="form-double">
          <div className="form-date">
            <MyDatePicker
              className="datepicker"
              date={canOrderDate}
              placeholder="日付を選択"
              dateFormat={dateFormat}
              onChange={(canOrderDate) =>
                setNotificationState((prev) => ({ ...prev, canOrderDate }))
              }
              isDisabled={!scheduledDate}
              showTimeSelect={false}
              minDate={minDate(scheduledDate)}
            />
          </div>
          <div className="form-time">
            <MyTimePicker
              date={canOrderDate ? canOrderTime : undefined}
              placeholder="時間を選択"
              dateFormat={timeFormat}
              onChange={(canOrderTime) =>
                setNotificationState((prev) => ({ ...prev, canOrderTime }))
              }
              isDisabled={!canOrderDate}
              showTimeSelect={false}
              minTime={minTime}
              maxTime={maxTime}
            />
          </div>
        </div>
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.can_order_time}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder labelName="受け渡し日を選択してください" isRequired />
        <div
          className={`day-picker ${
            publishedDate && scheduledDate ? "" : "is-disabled"
          }`}
        >
          <DayPicker
            selectedDays={lastPickupDays}
            month={initialLastPickupMonth}
            onDayClick={(day, { selected, disabled }) =>
              onClickLastPickupDay(day, selected, disabled)
            }
            disabledDays={disabledLastPickupDays}
            firstDayOfWeek={1}
            localeUtils={{
              ...LocaleUtils,
              formatMonthTitle: (v) => dayjs(v).format("YYYY年MM月"),
              formatWeekdayShort: (v) =>
                ["日", "月", "火", "水", "木", "金", "土"].find(
                  (s, i) => i === v
                ) || "",
            }}
          />
          <div className="day-tags">
            {lastPickupDays.map((v: Date | undefined, i: number) => (
              <div
                className="form-lastPickup form-date"
                key={`{lastPickupDays|${i}`}
              >
                <InputLabelHolder
                  labelName={`日程${i + 1}`}
                  isRequired={false}
                />
                <MyDatePicker
                  className="datepicker"
                  date={v}
                  dateFormat={dateFormat}
                  isDisabled={true}
                  minDate={minDate(scheduledDate)}
                />
                <MdClose
                  size={24}
                  onClick={() => onClickLastPickupDayRemove(i)}
                />
              </div>
            ))}
          </div>
        </div>
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.last_pickup_days}
        />
      </FormItem>
      <FormItem>
        <InputLabelHolder
          labelName="商品を受け渡す際の注意があればご記入ください"
          isRequired={false}
        />
        <TextArea
          rows={10}
          max={300}
          inputValue={pickupSupplement}
          placeholder={"注意事項を入力してください。"}
          onChange={(v) =>
            setNotificationState((prev) => ({
              ...prev,
              pickupSupplement: v.slice(0, 300),
            }))
          }
          isError={false}
          isDisabeld={false}
        />
        <ValidateMessageIfError
          marginVertical={8}
          messages={errors.pickup_supplement}
        />
      </FormItem>
      <ButtonWrapper>
        <Button
          isDisabled={false}
          buttonColor="green"
          clickEvent={wrapOnSubmit}
          text="入力内容を確認する"
        />
        <Button
          isDisabled={false}
          buttonColor="white"
          textColor="green"
          borderColor="green"
          clickEvent={wrapOnSave}
          text="下書きを一時保存する"
        />
      </ButtonWrapper>
      <ButtonWrapper>
        <PlainHomeButton text="編集をキャンセルしトップページに戻る" />
      </ButtonWrapper>
    </Container>
  );
};

const Container = styled.div`
  .form-bakedBread {
    margin-bottom: 48px;
  }

  .is-hidden {
    display: none;
  }
`;

const FormItem = styled.div`
  &:not(:first-child) {
    margin-top: 24px;
  }
  .annotation {
    color: #6a6a6a;
    font-size: 12px;
    margin-bottom: 8px;
  }

  .form-triple {
    display: flex;
    justify-content: space-between;

    > * {
      width: 33%;
    }
  }
  .form-double {
    display: flex;
    justify-content: space-between;

    > * {
      width: 49%;
    }
  }

  .form-date {
    .react-datepicker__tab-loop {
      /* width: 300px; */
    }
    .react-datepicker-popper {
      width: 250px;
      ${media.greaterThan("medium")`
        width: 300px;
      `};
      ${StyleConfig.MEDIAQUERY.lessThan("xs")`
        width: 220px;
      `};

      .react-datepicker__month-container {
        width: 100%;
      }
    }
    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range,
    .react-datepicker__month-text--selected,
    .react-datepicker__month-text--in-selecting-range,
    .react-datepicker__month-text--in-range,
    .react-datepicker__quarter-text--selected,
    .react-datepicker__quarter-text--in-selecting-range,
    .react-datepicker__quarter-text--in-range {
      background-color: ${StyleConfig.COLOR.GREEN};
    }

    .react-datepicker__day--keyboard-selected,
    .react-datepicker__month-text--keyboard-selected,
    .react-datepicker__quarter-text--keyboard-selected {
      border: 1px solid ${StyleConfig.COLOR.GREEN};
      color: ${StyleConfig.COLOR.TEXT_COLOR};
      background-color: transparent;
    }
  }

  .form-lastPickup {
    width: 100%;
    display: inline-flex;
    align-items: center;
    &:not(:last-child) {
      margin-bottom: 8px;
    }
    &:last-of-type {
      margin-bottom: 16px;
    }

    > * {
      &:first-child {
        margin-bottom: 0;
      }
      &:last-child {
        margin-left: 10px;
        margin-right: 10px;
        cursor: pointer;
      }
    }

    .label_holder {
      width: 60px;
    }

    .is-hidden {
      visibility: hidden;
    }
  }

  .button-lastPickup {
    width: 160px;
    margin: 8px auto 16px auto;
  }

  .day-picker {
    display: flex;
    justify-content: space-between;

    ${StyleConfig.MEDIAQUERY.lessThan("small")`
      flex-wrap: wrap;
      justify-content: center;
      background-color: #fff;
      width: 100%;
    `};

    .DayPicker {
      width: 49%;

      .DayPicker-wrapper {
        padding-bottom: 0;
      }

      .DayPicker-Months {
        background-color: #fff;
        width: 100%;

        .DayPicker-Month {
          width: 100%;
          margin: 1em;
        }
      }

      .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
        background-color: ${StyleConfig.COLOR.GREEN};
        /* padding: 12px; */
        border-radius: 100%;
        ${StyleConfig.MEDIAQUERY.lessThan("small")`
          // padding: 8px 10px;
        `};
      }
    }

    &.is-disabled {
      .DayPicker {
        cursor: not-allowed;

        .DayPicker-Months {
          pointer-events: none;
          background-color: ${StyleConfig.COLOR.INPUT_BORDER};

          .DayPicker-Day--disabled {
            color: ${StyleConfig.COLOR.TEXT_GRAY};
          }
        }
      }
    }

    .day-tags {
      width: 49%;
      ${StyleConfig.MEDIAQUERY.lessThan("small")`
        display: inline-flex;
        flex-wrap: wrap;
        width: 100%;
      `};

      .label_holder {
        text-align: center;
        display: inline-flex;
        justify-content: center;
      }
    }
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 36px;

  button {
    &:first-child {
      margin-bottom: 20px;
    }
  }
`;

export default ReservationCreateForm;
